/**
 * Shape + helpers for `file_upload` field values inside the schema-driven
 * intake form (see src/lib/schema-renderer/primitives/FileUploadR.tsx).
 *
 * A successfully uploaded file is stored in react-hook-form, and therefore
 * autosaved via intake_field_data, as this JSONB-safe metadata object, NEVER
 * the raw File/FileList (those aren't serialisable and can't round-trip
 * through Supabase). Everything here must survive a JSON round-trip.
 */

export interface UploadedFileValue {
  document_id: string;
  storage_path: string;
  filename: string;
  size_bytes: number;
  mime_type: string;
  uploaded_at: string;
}

/**
 * True only when `v` is a real upload-metadata object produced by a
 * successful uploadDocument() call (has both a document_id and a filename).
 *
 * Guards against:
 *   - empty values: undefined, null, ''.
 *   - legacy junk: the OLD unwired FileUploadR registered a raw
 *     `<input type="file">` with react-hook-form, so a bare FileList got
 *     serialised into intake_field_data. A File object has no own
 *     enumerable properties, so that serialisation comes back as an
 *     index-keyed object of empty objects (e.g. {"0":{}}), which is
 *     truthy and object-shaped but has no document_id.
 *   - any other object missing document_id (half-written / corrupt value).
 */
export function isUploadedFileValue(v: unknown): v is UploadedFileValue {
  if (v === null || v === undefined || typeof v !== 'object') return false;
  const obj = v as Record<string, unknown>;
  return (
    typeof obj.document_id === 'string' && obj.document_id.length > 0 &&
    typeof obj.filename === 'string' && obj.filename.length > 0
  );
}

/**
 * Human-readable file size, e.g. "1.4 MB". Mirrors the formatBytes() helper
 * in src/pages/Documents.tsx so the two upload surfaces read identically.
 */
export function formatFileSize(bytes: number | null | undefined): string {
  if (bytes === null || bytes === undefined || Number.isNaN(bytes) || bytes < 0) return '';
  if (bytes < 1024) return `${bytes} B`;
  if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(1)} KB`;
  return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
}
